import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import SearchSection from './components/SearchSection';
import UserRoleGuides from './components/UserRoleGuides';
import FaqSection from './components/FaqSection';
import TutorialLibrary from './components/TutorialLibrary';
import SupportContact from './components/SupportContact';
import ProgressTracker from './components/ProgressTracker';

const HelpDocumentation = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [userRole, setUserRole] = useState('agent'); // Default to agent view
  const [completedTutorials, setCompletedTutorials] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Navigation sections
  const navigationSections = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'Home' },
    { id: 'guides', label: 'Guides par Rôle', icon: 'Users' },
    { id: 'tutorials', label: 'Tutoriels', icon: 'PlayCircle' },
    { id: 'faq', label: 'FAQ', icon: 'HelpCircle' },
    { id: 'api', label: 'Documentation API', icon: 'Code' },
    { id: 'support', label: 'Support', icon: 'MessageSquare' }
  ];

  // User roles
  const userRoles = [
    { id: 'agent', label: 'Agents', icon: 'UserCheck', color: 'bg-blue-500' },
    { id: 'business', label: 'Apporteurs d\'affaires', icon: 'Briefcase', color: 'bg-green-500' },
    { id: 'venue', label: 'Venues', icon: 'MapPin', color: 'bg-purple-500' },
    { id: 'artist', label: 'Artistes', icon: 'Music', color: 'bg-orange-500' },
    { id: 'admin', label: 'Administrateurs', icon: 'Settings', color: 'bg-red-500' }
  ];

  useEffect(() => {
    // Load completed tutorials from localStorage
    const saved = localStorage.getItem('artisio_completed_tutorials');
    if (saved) {
      setCompletedTutorials(JSON.parse(saved));
    }
  }, []);

  const handleTutorialComplete = (tutorialId) => {
    const updated = [...completedTutorials, tutorialId];
    setCompletedTutorials(updated);
    localStorage.setItem('artisio_completed_tutorials', JSON.stringify(updated));
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setShowSearchResults(query?.length > 0);
    if (query?.length > 2) {
      // Trigger search functionality
      setActiveSection('search');
    }
  };

  const renderOverviewSection = () => (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-xl p-8">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center">
              <Icon name="BookOpen" size={32} className="text-accent-foreground" />
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Bienvenue dans la Documentation Artis.io
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              Découvrez comment utiliser efficacement la plateforme Artis.io pour maximiser vos performances 
              et optimiser votre expérience utilisateur. Notre documentation complète vous guide à travers 
              toutes les fonctionnalités disponibles.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button variant="default" onClick={() => setActiveSection('guides')}>
                <Icon name="Users" size={16} className="mr-2" />
                Explorer les Guides
              </Button>
              <Button variant="outline" onClick={() => setActiveSection('tutorials')}>
                <Icon name="PlayCircle" size={16} className="mr-2" />
                Voir les Tutoriels
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Access Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {userRoles?.map((role) => (
          <div key={role?.id} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer"
               onClick={() => { setUserRole(role?.id); setActiveSection('guides'); }}>
            <div className="flex items-center space-x-4 mb-4">
              <div className={`w-12 h-12 ${role?.color} rounded-lg flex items-center justify-center`}>
                <Icon name={role?.icon} size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-card-foreground">{role?.label}</h3>
                <p className="text-sm text-muted-foreground">Guide spécialisé</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Documentation spécifique pour les {role?.label?.toLowerCase()} avec des guides pratiques 
              et des exemples concrets.
            </p>
          </div>
        ))}
      </div>

      {/* Features Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Icon name="Zap" size={24} className="text-warning" />
            <h3 className="text-xl font-semibold text-card-foreground">Fonctionnalités Clés</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-center space-x-3">
              <Icon name="Check" size={16} className="text-success" />
              <span className="text-muted-foreground">Gestion des campagnes marketing</span>
            </li>
            <li className="flex items-center space-x-3">
              <Icon name="Check" size={16} className="text-success" />
              <span className="text-muted-foreground">Suivi des performances en temps réel</span>
            </li>
            <li className="flex items-center space-x-3">
              <Icon name="Check" size={16} className="text-success" />
              <span className="text-muted-foreground">Génération de liens personnalisés</span>
            </li>
            <li className="flex items-center space-x-3">
              <Icon name="Check" size={16} className="text-success" />
              <span className="text-muted-foreground">Analytics avancées</span>
            </li>
            <li className="flex items-center space-x-3">
              <Icon name="Check" size={16} className="text-success" />
              <span className="text-muted-foreground">Gestion des commissions automatisée</span>
            </li>
          </ul>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Icon name="Target" size={24} className="text-accent" />
            <h3 className="text-xl font-semibold text-card-foreground">Objectifs de la Plateforme</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-center space-x-3">
              <Icon name="ArrowRight" size={16} className="text-accent" />
              <span className="text-muted-foreground">Optimiser les partenariats musicaux</span>
            </li>
            <li className="flex items-center space-x-3">
              <Icon name="ArrowRight" size={16} className="text-accent" />
              <span className="text-muted-foreground">Faciliter la collaboration entre acteurs</span>
            </li>
            <li className="flex items-center space-x-3">
              <Icon name="ArrowRight" size={16} className="text-accent" />
              <span className="text-muted-foreground">Améliorer la transparence financière</span>
            </li>
            <li className="flex items-center space-x-3">
              <Icon name="ArrowRight" size={16} className="text-accent" />
              <span className="text-muted-foreground">Proposer des outils d'analyse performants</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'overview':
        return renderOverviewSection();
      case 'guides':
        return <UserRoleGuides userRole={userRole} onRoleChange={setUserRole} />;
      case 'tutorials':
        return <TutorialLibrary 
          completedTutorials={completedTutorials}
          onTutorialComplete={handleTutorialComplete}
          userRole={userRole}
        />;
      case 'faq':
        return <FaqSection userRole={userRole} />;
      case 'api':
        return renderApiDocumentation();
      case 'support':
        return <SupportContact />;
      case 'search':
        return renderSearchResults();
      default:
        return renderOverviewSection();
    }
  };

  const renderApiDocumentation = () => (
    <div className="space-y-8">
      <div className="bg-card border border-border rounded-xl p-6">
        <h2 className="text-2xl font-bold text-card-foreground mb-4">Documentation API</h2>
        <p className="text-muted-foreground mb-6">
          Intégrez Artis.io avec vos systèmes existants grâce à notre API RESTful.
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-card-foreground">Endpoints Principaux</h3>
            <div className="space-y-3">
              <div className="bg-muted/50 p-4 rounded-lg">
                <code className="text-sm font-mono">GET /api/v1/partners</code>
                <p className="text-sm text-muted-foreground mt-1">Récupérer la liste des partenaires</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <code className="text-sm font-mono">POST /api/v1/campaigns</code>
                <p className="text-sm text-muted-foreground mt-1">Créer une nouvelle campagne</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <code className="text-sm font-mono">GET /api/v1/analytics</code>
                <p className="text-sm text-muted-foreground mt-1">Accéder aux données d'analyse</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-card-foreground">Authentification</h3>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-3">
                Utilisez un token Bearer pour authentifier vos requêtes :
              </p>
              <code className="text-sm font-mono bg-background p-2 rounded border">
                Authorization: Bearer YOUR_API_TOKEN
              </code>
            </div>
            
            <div className="mt-6">
              <Button variant="outline" className="w-full">
                <Icon name="Download" size={16} className="mr-2" />
                Télécharger la Documentation Complète
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSearchResults = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-6">
        <h2 className="text-xl font-semibold text-card-foreground mb-4">
          Résultats de recherche pour "{searchQuery}"
        </h2>
        {/* Search results would be populated based on the search query */}
        <div className="space-y-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h3 className="font-medium text-card-foreground mb-2">Guide : Création de campagne</h3>
            <p className="text-sm text-muted-foreground">
              Apprenez à créer et configurer vos campagnes marketing sur Artis.io...
            </p>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <h3 className="font-medium text-card-foreground mb-2">FAQ : Gestion des commissions</h3>
            <p className="text-sm text-muted-foreground">
              Questions fréquentes sur le système de commissions...
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Aide & Documentation</h1>
                <p className="text-muted-foreground">
                  Trouvez toutes les informations nécessaires pour utiliser Artis.io efficacement
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <ProgressTracker completed={completedTutorials?.length || 0} total={12} />
              </div>
            </div>
          </div>

          {/* Search Section */}
          <div className="mb-8">
            <SearchSection 
              onSearch={handleSearch} 
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-xl p-6 sticky top-24">
                <h3 className="font-semibold text-card-foreground mb-4">Navigation</h3>
                <nav className="space-y-2">
                  {navigationSections?.map((section) => (
                    <button
                      key={section?.id}
                      onClick={() => setActiveSection(section?.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                        activeSection === section?.id
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={section?.icon} size={18} />
                      <span className="text-sm font-medium">{section?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {renderActiveSection()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpDocumentation;